import Swiper from 'swiper/bundle';

export default class Carousel {
  constructor(element) {
    this.element = element;
    this.eqDebounce = null;
    this.io = null; 
    this.ro = null;

    this.defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      autoHeight: false,
      preloadImages: true,
      observer: true,
      observeParents: true,
      pagination: {
        el: this.element.querySelector('.swiper-pagination'),
        type: 'bullets',
      },
      navigation: {
        nextEl: this.element.querySelector('.custom-next'),
        prevEl: this.element.querySelector('.custom-prev'),
      },
      on: {
        init: () => this.stabilize(),
        imagesReady: () => this.stabilize(0),
        resize: () => this.stabilize(),
        breakpoint: () => this.stabilize(),
        update: () => this.stabilize(),
        slidesLengthChange: () => this.stabilize(),
        observerUpdate: () => this.stabilize(),
        slideChangeTransitionEnd: () => this.stabilize(),
        loopFix: () => this.stabilize(),
      },
    };

    this.spaceBetween = { spaceBetween: 24 };
    this.navCheck();
    this.init();
  }

  navCheck() {
  if (!this.element) return;

  const parent = this.element.closest('.row');

  const customNext = parent?.querySelector('.custom-next');
  const customPrev = parent?.querySelector('.custom-prev');

  if (customNext && customPrev) {
    this.defaultOptions.navigation = {
      nextEl: customNext,
      prevEl: customPrev,
    };
    return;
  }

  const fallbackNext = this.element.querySelector('.swiper-button-next');
  const fallbackPrev = this.element.querySelector('.swiper-button-prev');

  if (fallbackNext && fallbackPrev) {
    this.defaultOptions.navigation = {
      nextEl: fallbackNext,
      prevEl: fallbackPrev,
    };
  } else {
    this.defaultOptions.navigation = false;
  }
}


  resizeSlides() {
    const slides = Array.from(
      this.element.querySelectorAll(
        '.swiper-slide:not(.swiper-slide-duplicate)'
      )
    );
    if (!slides.length) return;

    const visible = this.element.getClientRects().length > 0;
    if (!visible) {
      this.stabilize();
      return;
    }

    let maxHeight = 0;

    slides.forEach((slide) => {
      slide.style.setProperty('height', 'auto', 'important');
    });

    slides.forEach((slide) => {
      const h = slide.offsetHeight; 
      if (h > maxHeight) maxHeight = h;
    });

    if (!maxHeight) return;
    const H = `${Math.ceil(maxHeight)}px`;

    slides.forEach((slide) => {
      slide.style.setProperty('height', H, 'important'); 
    });
  }

  stabilize(delay = 60, passes = 3) {
    if (this.eqDebounce) clearTimeout(this.eqDebounce);
    this.eqDebounce = setTimeout(() => {
      let i = 0;
      const tick = () => {
        this.resizeSlides();
        i += 1;
        if (i < passes) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
  }

  // écoute le load des images (si pas déjà complètes)
  watchImages() {
    const imgs = this.element.querySelectorAll('img');
    imgs.forEach((img) => {
      if (img.complete) return;
      const again = () => this.stabilize(0);
      img.addEventListener('load', again, { once: true });
      img.addEventListener('error', again, { once: true });
    });
  }

  // observe visibilité (tabs/accordéons en display:none)
  watchVisibility() {
    if (typeof IntersectionObserver === 'undefined') return;
    if (this.io) this.io.disconnect();
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.stabilize(0);
        });
      },
      { threshold: 0.01 }
    );
    this.io.observe(this.element);
  }

  // observe changements de taille des slides/contenu
  watchSizeChanges() {
    if (typeof ResizeObserver === 'undefined') return;
    if (this.ro) this.ro.disconnect();
    this.ro = new ResizeObserver(() => this.stabilize(0));
    const wrapper = this.element.querySelector('.swiper-wrapper');
    if (wrapper) this.ro.observe(wrapper);
    this.element
      .querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
      .forEach((s) => this.ro.observe(s));
  }

  bindGlobals() {
    window.addEventListener('load', () => this.stabilize(0));
    window.addEventListener('orientationchange', () => this.stabilize(0));
    if (document.fonts?.ready)
      document.fonts.ready.then(() => this.stabilize(0));
  }

  init() {
    let options = this.defaultOptions;

    switch (this.element.dataset.carousel) {
      case 'auto':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          loop: true,
          autoplay: { delay: 3000, disableOnInteraction: false },
          slidesPerView: 'auto',
          spaceBetween: 10,
        };
      break;
      case 'two':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: { 768: { slidesPerView: 2 } },
        };
        break;
      case '2.25':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          autoHeight: true,
           loop: true,
          slidesPerView: 1,
          breakpoints: {
            768: { slidesPerView: 1 },
            1200: { slidesPerView: 2.25 },
          },
        };
        break;
      case 'three':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: {
            768: { slidesPerView: 2 },
            1200: { slidesPerView: 2.5 },
            1440: { slidesPerView: 3 },
          },
        };
        break;
      case '3.25':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          centeredSlides: false,
          watchOverflow: true,
          slidesPerView: 1.25,
          slidesPerGroup: 1,
           loop: true,
          resistanceRatio: 0,
          allowTouchMove: true,
          slideToClickedSlide: true,
          allowSlideNext: true,
          watchOverflow: false,
          breakpoints: {
            768: { slidesPerView: 2.25 },
            1200: { slidesPerView: 3.25 },
          },
        };
        break;
      case 'four':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: {
            544: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1200: { slidesPerView: 4 },
          },
        };
        break;
      case 'five':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: {
            544: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
            1200: { slidesPerView: 5 },
          },
        };
        break;
      case 'six':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: {
            544: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
            1100: { slidesPerView: 5 },
            1300: { slidesPerView: 6 },
          },
        };
        break;
      case 'footer':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          slidesPerView: 1,
          breakpoints: {
            544: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
            1400: { slidesPerView: 6 },
            1600: { slidesPerView: 7 },
            1800: { slidesPerView: 8 },
          },
          autoplay: { delay: 3000 },
        };
        break;
      case 'logos':
        options = {
          ...this.defaultOptions,
          ...this.spaceBetween,
          loop: true,
          autoplay: { delay: 3000, disableOnInteraction: false },
          slidesPerView: 2,
          breakpoints: {
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
            1400: { slidesPerView: 6 },
          },
        };
        break;
    }

    this.swiper = new Swiper(this.element, options);

    this.bindGlobals();
    this.watchImages();
    this.watchVisibility();
    this.watchSizeChanges();

    // passes post-init
    requestAnimationFrame(() => this.resizeSlides());
    setTimeout(() => this.resizeSlides(), 80);
    setTimeout(() => this.resizeSlides(), 200);
  }
}
